import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, API } from "@/lib/api";
import { FileText, Users, Receipt, Pulse, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_LABELS = {
  none: "Sin plan",
  start: "AS START",
  evolution: "AS EVOLUTION",
  elite: "AS ELITE",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [s, c, e] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/clients"),
      api.get("/evaluations"),
    ]);
    setStats(s.data);
    setClients(c.data);
    setEvaluations(e.data);
  };

  useEffect(() => {
    load().catch((err) => console.error("[Admin] load:", err)).finally(() => setLoading(false));
  }, []);

  const assignPlan = async (userId, plan, days = 30) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/plan`, { plan, days });
      toast.success(`Plan ${PLAN_LABELS[data.plan]} activado`);
      if (data.email_result?.status === "sent") {
        toast.success("Email de bienvenida enviado", { description: `ID: ${data.email_result.email_id}` });
      } else if (data.email_result?.status === "skipped") {
        toast.info("Email no enviado", { description: "RESEND_API_KEY no configurada" });
      } else if (data.email_result?.status === "error") {
        toast.error("Error al enviar email", { description: data.email_result.error });
      }
      await load();
    } catch (err) {
      console.error("[Admin] assignPlan:", err);
      toast.error("No se pudo asignar el plan");
    }
  };

  const resendWelcome = async (userId, name) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/resend-welcome`);
      const r = data.email_result;
      if (r?.status === "sent") {
        toast.success(`Bienvenida reenviada a ${name}`, { description: `ID: ${r.email_id}` });
      } else if (r?.status === "skipped") {
        toast.info("Email no enviado", { description: "RESEND_API_KEY no configurada" });
      } else {
        toast.error("Fallo al reenviar", { description: r?.error || "" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reenviar");
    }
  };

  if (loading) return <Layout><div className="p-16 text-as-gray">Cargando admin...</div></Layout>;

  const cards = [
    { label: "Clientes", value: stats?.clients || 0, icon: Users },
    { label: "Evaluaciones", value: stats?.evaluations || 0, icon: FileText },
    { label: "Seguimientos", value: stats?.tracking_entries || 0, icon: Pulse },
    { label: "Pagos completados", value: stats?.paid_transactions || 0, icon: Receipt },
  ];

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="as-label text-as-green mb-3">PANEL DEL COACH</p>
          <h1 className="as-heading text-5xl text-white" data-testid="admin-title">
            CENTRO DE <span className="text-as-green">MANDO.</span>
          </h1>
        </div>
      </section>

      <section className="bg-as-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {cards.map((c) => (
              <div key={c.label} className="as-card" data-testid={`stat-${c.label.toLowerCase()}`}>
                <c.icon size={28} weight="duotone" color="#39FF14" className="mb-3" />
                <p className="as-label mb-1">{c.label}</p>
                <p className="as-heading text-5xl text-white">{c.value}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="as-card">
              <h2 className="as-heading text-2xl text-white mb-4">CLIENTES & PLANES</h2>
              <p className="text-xs text-as-gray mb-4">
                Después de recibir el pago por Zelle o Venmo, asigna el plan al cliente desde aquí.
                Al asignar EVOLUTION o ELITE, se envía automáticamente el email de bienvenida.
              </p>
              {clients.length === 0 ? (
                <p className="text-as-gray text-sm">Sin clientes aún.</p>
              ) : (
                <div className="divide-y divide-as-border">
                  {clients.map((c) => (
                    <ClientRow key={c.user_id} client={c} onAssign={assignPlan} onResend={resendWelcome} />
                  ))}
                </div>
              )}
            </div>

            <div className="as-card">
              <h2 className="as-heading text-2xl text-white mb-4">EVALUACIONES RECIENTES</h2>
              {evaluations.length === 0 ? (
                <p className="text-as-gray text-sm">Sin evaluaciones aún.</p>
              ) : (
                <div className="divide-y divide-as-border">
                  {evaluations.slice(0, 10).map((e) => (
                    <div key={e.id} className="py-3 flex items-center justify-between gap-3" data-testid={`eval-${e.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-semibold truncate">{e.full_name}</p>
                        <p className="text-xs text-as-gray">
                          {e.objective} · {e.training_level} · {e.created_at?.slice(0, 10)}
                        </p>
                      </div>
                      <a
                        href={`${API}/evaluations/${e.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="as-label text-as-green flex items-center gap-1"
                        data-testid={`download-eval-${e.id}`}
                      >
                        PDF <DownloadSimple size={14} weight="bold" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

const ClientRow = ({ client, onAssign, onResend }) => {
  const [plan, setPlan] = useState(client.plan || "none");
  const [days, setDays] = useState(30);
  const expires = client.plan_expires_at?.slice(0, 10);
  const activePlan = client.plan && client.plan !== "none" ? PLAN_LABELS[client.plan] : "Sin plan";
  const isPremium = client.plan === "evolution" || client.plan === "elite";

  return (
    <div className="py-4 space-y-2" data-testid={`client-${client.user_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold truncate">{client.name}</p>
          <p className="text-xs text-as-gray truncate">{client.email}</p>
        </div>
        <div className="text-right">
          <p className="as-label text-as-green text-[10px]">{activePlan}</p>
          {expires && <p className="text-[10px] text-as-gray-dim">vence {expires}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center pt-2">
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="as-input !py-1 !text-xs flex-1 min-w-[100px]"
          data-testid={`select-plan-${client.user_id}`}
        >
          <option value="none">Sin plan</option>
          <option value="start">AS START</option>
          <option value="evolution">AS EVOLUTION</option>
          <option value="elite">AS ELITE</option>
        </select>
        <input
          type="number"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="as-input !py-1 !text-xs w-20"
          title="Días de vigencia"
          min="1"
          max="365"
          data-testid={`days-${client.user_id}`}
        />
        <button
          onClick={() => onAssign(client.user_id, plan, days)}
          className="as-btn-primary !py-1 !px-3 !text-[10px]"
          data-testid={`assign-${client.user_id}`}
        >
          ACTIVAR
        </button>
        {isPremium && (
          <button
            onClick={() => onResend(client.user_id, client.name)}
            className="!py-1 !px-2 !text-[10px] border border-as-border text-as-gray hover:text-as-green hover:border-as-green flex items-center gap-1 uppercase tracking-widest font-bold"
            title="Reenviar email de bienvenida"
            data-testid={`resend-${client.user_id}`}
          >
            <EnvelopeSimple size={11} weight="bold" /> Reenviar
          </button>
        )}
      </div>
    </div>
  );
};
